import ActionFeedbackModal from '@/components/ActionFeedbackModal';
import Constants from '@/Constants';
import { Report } from '@/models/Report';
import { ReportTranslation } from '@/models/ReportTranslation';
import DateRangeModal from '@/pages/Data/Reports/DateRangeModal';
import ReportHistoryModal from '@/pages/Data/Reports/ReportHistoryModal';
import { ApplicationState } from '@/store';
import { downloadLastReportRequest, generateAsyncReportRequest, listReportProcessesRequest } from '@/store/report/action';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import './style.scss';

export interface SelectionType {
    startDate?: Date;
    endDate?: Date;
    key: string;
}

export const initialDateRange: SelectionType = {
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection',
};

interface Props {
    report: Report;
    hasPendingProcess: boolean;
    setRefreshInterval: CallableFunction;
}

const ReportCard: React.FC<Props> = ({ report, hasPendingProcess, setRefreshInterval }: Props) => {
    const {
        strings,
        organizationId,
        applicationId,
        loading,
        reportProcesses,
        successOnGenerateAsyncReport,
        errorOnGenerateAsyncReport,
        currentReportId,
    } = useSelector((state: ApplicationState) => {
        return {
            strings: state.locale.data,
            organizationId: state.identity.data.user.orgId,
            applicationId: state.identity.data.user.applicationId,
            loading: state.report.loading,
            reportProcesses: state.report.data,
            successOnGenerateAsyncReport: state.report.successOnGenerateAsyncReport,
            errorOnGenerateAsyncReport: state.report.errorOnGenerateAsyncReport,
            currentReportId: state.report.currentReportId,
        };
    });

    const dispatch = useDispatch();

    const hasDateFilter = report.reportFilters.find((filter) => filter.type === 'DATE_RANGE');

    const [showDateRangeModal, setShowDateRangeModal] = useState<boolean>(false);
    const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
    const [selectedDateRange, setSelectedDateRange] = useState<SelectionType>(initialDateRange);
    const [currentTranslation, setCurrentTranslation] = useState<ReportTranslation>();
    const [disableReportButton, setDisableReportButton] = useState<boolean>(false);
    const [isAwaitingResponse, setIsAwaitingResponse] = useState<boolean>(false);
    const [showDescriptionTooltip, setShowDescriptionTooltip] = useState<boolean>(false);
    const [hasPendingDownload, setHasPendingDownload] = useState<boolean>(false);


    useEffect(() => {
        if (report.reportTranslations) {
            let translation = report.reportTranslations.find((item) => item.locale === strings._LANGUAGE);

            if (!translation) {
                // Get default locale
                translation = report.reportTranslations.find((item) => item.locale === Constants.language.PT);
            }

            setCurrentTranslation(translation);
        }
    }, [strings._LANGUAGE]);

    useEffect(() => {
        dispatch(listReportProcessesRequest(report.id));
        setIsAwaitingResponse(hasPendingProcess);       
    }, []);

    useEffect(() => {     
        setIsAwaitingResponse(hasPendingProcess);  
    }, [hasPendingProcess])

    useEffect(() => {       
        setDisableReportButton(isAwaitingResponse);
    }, [isAwaitingResponse])

    useEffect(() => {
        if (!disableReportButton && hasPendingDownload) {            
            setHasPendingDownload(false);
            // get download
            dispatch(downloadLastReportRequest(report.id));
        }
    }, [reportProcesses, disableReportButton, hasPendingDownload]);

    const generateReportHandler = () => {
        setDisableReportButton(true);
        setIsAwaitingResponse(true);
        setHasPendingDownload(true);
        setRefreshInterval(10000);
        let customFilters = null;
        if (hasDateFilter) {
            const { startDate, endDate } = selectedDateRange;

            customFilters = {
                Organizacao: organizationId,
                PeriodoDados: [moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD') + ' 23:59:59'],
            };

            setShowDateRangeModal(false);
            setSelectedDateRange(initialDateRange);
        }

        dispatch(
            generateAsyncReportRequest({
                reportId: report.id,
                applicationId,
                organizationId,
                customFilters: customFilters ? JSON.stringify(customFilters) : null,
            }),
        );
    };

    const toggleHistoryModal = () => {
        setShowHistoryModal(!showHistoryModal);
    };

    const toggleDateRangeModal = () => {
        if (showDateRangeModal) {
            setRefreshInterval(10000);
            setSelectedDateRange(initialDateRange);
        } else {
            setRefreshInterval(0);
        }

        setShowDateRangeModal(!showDateRangeModal);
    };

    return (
        <>
            {currentReportId === report.id && (
                <ActionFeedbackModal
                    error={errorOnGenerateAsyncReport}
                    errorMessage={strings.DATA_PAGE.GENERATE_ERROR_MESSAGE}
                    loading={loading}
                    onRetry={generateReportHandler}
                    success={successOnGenerateAsyncReport}
                    successMessage={strings.DATA_PAGE.GENERATE_SUCCESS_MESSAGE}
                    title={strings.DATA_PAGE.TRY_AGAIN}
                />
            )}

            {showHistoryModal && (
                <ReportHistoryModal close={toggleHistoryModal} reportId={report.id} reportName={currentTranslation?.name} show={showHistoryModal} />
            )}

            <DateRangeModal
                close={toggleDateRangeModal}
                confirmAction={generateReportHandler}
                selectedDateRange={selectedDateRange}
                setSelectedDateRange={setSelectedDateRange}
                show={showDateRangeModal}
            />

            <div className="report-card">
                <OverlayTrigger
                    overlay={
                        <Tooltip className="report-tooltip" id={`tooltip-name${report.id}`}>
                            <span>{currentTranslation?.name}</span>
                        </Tooltip>
                    }
                    placement="bottom"
                >
                    <h3>{currentTranslation?.name}</h3>
                </OverlayTrigger>
                <OverlayTrigger
                    onToggle={setShowDescriptionTooltip}
                    overlay={
                        <Tooltip className="report-tooltip" id={`tooltip-description${report.id}`}>
                            <span>{currentTranslation?.description}</span>
                        </Tooltip>
                    }
                    placement="bottom"
                    show={currentTranslation?.description ? showDescriptionTooltip : false}
                >
                    <span>{currentTranslation?.description}</span>
                </OverlayTrigger>
                <div className="report-card-actions">
                    {disableReportButton ? (
                        <button className="btn btn-report btn-outline-report">
                            <Spinner animation="border" className="mr-3" size="sm" />
                            {strings.DATA_PAGE.REPORT_IN_PROCESS}
                        </button>
                    ) : (
                        <>
                            <button
                                className="btn btn-report btn-generate-report"
                                onClick={hasDateFilter ? toggleDateRangeModal : generateReportHandler}
                            >
                                {strings.DATA_PAGE.GENERATE}
                            </button>
                            <button className="btn btn-report btn-outline-report" onClick={toggleHistoryModal}>
                                {strings.DATA_PAGE.REPORTING_HISTORY}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default ReportCard;
